import { FunctionComponent, useState } from 'react'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import { useTranslation } from 'react-i18next'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import { useMutation } from '@apollo/client'
import styles from './createClientApi.module.scss'
import UserInput from '@/components/_custom/UserInput/userInput'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import { CREATE_API_CLIENT } from '@/modules/API/apiClient/apiClientHandler.ts'
import SelectMultiple from '@/components/_custom/Selector/selectMultiple/selectMultiple.tsx'
import { SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select.ts'
import { useAuth } from '@/modules/context/AuthContext.tsx'
import { Daycare } from '@/types/Daycare.ts'

interface CreateClientApiDialogProps {
    open: boolean
    handleClose: () => void
    refetchClientApi: () => void
}

const CreateClientApiDialog: FunctionComponent<CreateClientApiDialogProps> = ({
    open,
    handleClose,
    refetchClientApi
}: CreateClientApiDialogProps) => {
    const { t } = useTranslation()

    const { user } = useAuth()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [clientEmail, setClientEmail] = useState<string>('')
    const [validEmail, setValidEmail] = useState<boolean>(false)

    const [clientName, setClientName] = useState<string>('')

    const [daycareIds, setDaycareIds] = useState<string[]>([])

    const [createDaycareTrustee] = useMutation(CREATE_API_CLIENT, {
        variables: {
            email: clientEmail,
            name: clientName,
            daycareIds: daycareIds.length === 0 ? [] : daycareIds.map((id: string): number => Number(id))
        },
        onCompleted: () => {
            showNotificationSuccess(t('apiClientAdded'))

            refetchClientApi()
            handleClose()
        },
        onError: () => {
            showNotificationError(t('errorAddingApiClient'))
        }
    })

    const daycareIdsOptions: SelectOptionsDeprecated = [
        {
            options:
                user?.daycares.map(
                    (daycare: Daycare): SelectOptionDeprecated => ({
                        name: daycare.city + ' - ' + daycare.name,
                        value: daycare.id.toString()
                    })
                ) || []
        }
    ]

    return (
        <Dialog open={open} handleClose={handleClose}>
            <DialogHeader title={t('createApiClient')} />
            <DialogBody className={styles.DialogBody}>
                <div className={styles.InputContainer}>
                    <UserInput
                        label={t('name')}
                        className={styles.UserInput}
                        value={clientName}
                        setValue={setClientName}
                        format={'pascal'}
                        required
                    />
                    <UserInput
                        label={t('email')}
                        className={styles.UserInput}
                        value={clientEmail}
                        setValue={setClientEmail}
                        verify={'email'}
                        type='text'
                        unallowedCharacters={'email'}
                        setValid={setValidEmail}
                        required
                    />
                    <SelectMultiple
                        selectOptions={daycareIdsOptions}
                        selectValues={daycareIds}
                        setSelectValues={setDaycareIds}
                        className={styles.UserInput}
                        label={t('structures')}
                    />
                </div>
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon type={'close'} onClick={handleClose} className={styles.Button} />
                    <ButtonTextIcon
                        type={'create'}
                        onClick={createDaycareTrustee}
                        disabled={!validEmail || !clientEmail || !clientName}
                        className={styles.Button}
                    />
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default CreateClientApiDialog
